import {IDropDownItemViewModel} from "../item-model/drop-down-item-view-model.interface";
import {makeObservable, observable, runInAction} from "mobx";
import {IDropDownListViewModel} from "./drop-down-list-view-model.interface";
import {IServiceFactory} from "../../../../services/service-factory.interface";
import {IDropDownController} from "../../drop-down-controller.interface";

export interface IDropDownListModelOptions<TItem extends IDropDownItemViewModel = IDropDownItemViewModel> {
    services: IServiceFactory;
    allItems: TItem[];
    getSelectedItem: () => TItem | null;
    onItemSelected: (item: TItem) => void;
    placeholder?: string;
}

export class DropDownListModel<TItem extends IDropDownItemViewModel = IDropDownItemViewModel> implements IDropDownListViewModel<TItem> {
    constructor(private options: IDropDownListModelOptions<TItem>) {
        this.items = this.options.allItems;
        this._currentInputText = (this.options.getSelectedItem()?.formatText() ?? "");
        makeObservable(this, {
            items: observable.ref,
            _currentFocusedItemIndex: observable.ref,
            _currentInputText: observable.ref,
            isDropDownOpen: observable.ref
        });
    }

    items: TItem[];
    _currentFocusedItemIndex: number = 0;

    get currentFocusedItemIndex(): number {
        return this._currentFocusedItemIndex;
    }

    set currentFocusedItemIndex(value: number) {
        runInAction(() => {
            this._currentFocusedItemIndex = Math.min(Math.max(0, value), this.items.length - 1);
        });
    }

    get selectedItem(): TItem | null {
        return this.options.getSelectedItem();
    }

    selectItem(item: TItem): void {
        this.options.onItemSelected(item);
        this._dropDownController?.closeDropDown();
    }

    _currentInputText: string = "";
    get currentInputText(): string {
        return this._currentInputText;
    }

    set currentInputText(value: string) {
        runInAction(() => {
            value = value ?? "";
            if(this._currentInputText !== value) {
                this._currentInputText = value || "";
                this._applyFilter(this._currentInputText);
                this._dropDownController?.openDropDown();
            }
        });

    }

    get shouldRenderSearchInDropDownList(): boolean {
        return this.options.services.application.isInMobileView;
    }

    get inputPlaceholder(): string {
        if(this.isDropDownOpen) {
            if(this.shouldRenderSearchInDropDownList) {
                return this.options.placeholder ?? "";
            } else {
                return this.selectedItem?.formatText() ?? this.options.placeholder ?? "";
            }
        } else {
            return this.options.placeholder ?? "";
        }
    }

    private _dropDownController: IDropDownController | null = null;
    setDropDownController(controller: IDropDownController): void {
        this._dropDownController = controller;
    }

    private _applyFilter(filter: string): void {
        runInAction(() => {
            filter = (filter ?? '').toLowerCase().trim();

            if(filter) {
                this.items = this.options.allItems.filter(item => item.applyFilter(filter));
            } else {
                this.items = this.options.allItems;
            }

            if(this.currentFocusedItemIndex > this.items.length - 1) {
                this.currentFocusedItemIndex = 0;
            }
        });
    }

    private _moveUp(): void {
        if(this.currentFocusedItemIndex > 0) {
            runInAction(() => {
                this.currentFocusedItemIndex--;
            });
        }
    }

    private _moveDown(): void {
        if(this.currentFocusedItemIndex < this.items.length - 1) {
            runInAction(() => {
                this.currentFocusedItemIndex++;
            });
        }
    }


    private _onDocumentKeyDownHandler = (event: KeyboardEvent) => {
        if(event.key === "ArrowDown") {
            this._moveDown();
        } else if(event.key === "ArrowUp") {
            this._moveUp();
        } else if(event.key === 'Enter') {
            const item = this.items[this.currentFocusedItemIndex];
            if(item) {
                this.selectItem(item);
            }
        }
    }

    isDropDownOpen: boolean = false;
    onDropDownOpen() {
        document.addEventListener('keydown', this._onDocumentKeyDownHandler);
        runInAction(() => {
            this.isDropDownOpen = true;
            // we don't set the _currentInputText using the setter (this.currentInputText = "")
            // because in the setter we call this._dropDownController?.openDropDown(); and we will end up with
            // stack overflow exception
            this._currentInputText = "";
            this._applyFilter("");
        });

    }

    onDropDownClose() {
        document.removeEventListener('keydown', this._onDocumentKeyDownHandler);
        runInAction(() => {
            this.isDropDownOpen = false;
            this._currentInputText = this.selectedItem?.formatText() ?? "";
        });
    }
}
